import React, { useEffect, useMemo, useState } from 'react';
import { Box, useTheme, Icons, IconButton } from '@ublend-npm/aulaui-core';
import { Post } from '@app/types/core';
import useBreakpoints from '@app/hooks/useBreakpoints';
import pushRoute from '@app/utils/routing';
import { getCommonRoomPath } from '@app/actions/navigation';
import { getCurrentSpaceId } from '@core/selectors/space';
import { accessingCurrentSpaceAsStaff } from '@core/selectors/classroom';
import { COMMON_ROOM } from '@core/constants/postFeedType';
import { useSelector } from 'react-redux';
import { createStyles } from './CommonRoomResponsive.styles';
import CommonRoomPostCard from './CommonRoomPostCard.container';
import CommonRoomTabs, { CommonRoomTab } from './CommonRoomTabs';
import ComposePost from './ComposePost.container';
import StudentAction from './StudentAction/StudentAction';
import ScheduledPostsSummaryContainer from './ScheduledPostsSummary';
import type { CreatePostAuthor } from '../Shared/ComposePost/ComposePost';

interface CommonRoomFeedResponsiveProps {
  posts?: Post[];
  mobilePosts?: Post[];
  pinnedPosts?: Post[];
  recentPinnedPosts?: Post[];
  readOnly?: boolean;
  pinnedCount?: number;
  author?: CreatePostAuthor;
  onTabChange?: (tab: CommonRoomTab) => void;
}

const CommonRoomFeedResponsive = ({
  posts = [],
  mobilePosts = [],
  pinnedPosts = [],
  recentPinnedPosts = [],
  readOnly = false,
  pinnedCount,
  onTabChange,
}: CommonRoomFeedResponsiveProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { md } = useBreakpoints();
  const [activeTab, setActiveTab] = useState<CommonRoomTab>('feed');
  const spaceId = useSelector(getCurrentSpaceId);
  const isStaffAccess = useSelector(accessingCurrentSpaceAsStaff);

  useEffect(() => {
    if (md) {
      setActiveTab('feed');
    }
  }, [md]);

  const handleTabChange = (tab: CommonRoomTab) => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const isPinnedSelected = activeTab === 'pinned';
  const recentPinnedPostIds = useMemo(
    () => new Set(recentPinnedPosts.map((post) => post.objectId)),
    [recentPinnedPosts]
  );
  const orderedPinnedPosts = useMemo(
    () => [
      ...recentPinnedPosts,
      ...pinnedPosts.filter((post) => !recentPinnedPostIds.has(post.objectId)),
    ],
    [pinnedPosts, recentPinnedPostIds, recentPinnedPosts]
  );
  let displayedPosts = posts;
  if (!md) {
    displayedPosts = isPinnedSelected ? orderedPinnedPosts : mobilePosts;
  }
  const showFeedOnlyContent = md || !isPinnedSelected;
  const commonRoomPath = getCommonRoomPath(spaceId);
  // handle compose post mobile
  const handleComposePostMobile = () => {
    pushRoute(`${commonRoomPath}/new`);
  };

  return (
    <Box sx={styles.feedLayout} data-testid="responsive-feed-layout">
      {md && !isStaffAccess && (
        <Box
          component="section"
          aria-label="Post composer"
          sx={styles.composerRegion}
        >
          <ComposePost feedType={COMMON_ROOM} />
        </Box>
      )}
      {!md && (
        <Box
          component="section"
          aria-label="Feed controls"
          sx={styles.feedControlsRegion}
        >
          <CommonRoomTabs
            activeTab={activeTab}
            pinnedCount={pinnedCount}
            onTabChange={handleTabChange}
          />
          {!isStaffAccess && (
            <IconButton
              aria-label="Compose post"
              onClick={handleComposePostMobile}
              sx={styles.createPostButton}
            >
              <Icons.DriveFileRenameOutline />
            </IconButton>
          )}
        </Box>
      )}
      <Box
        component="section"
        aria-label={
          isPinnedSelected ? 'Pinned Common Room posts' : 'Common Room feed'
        }
        sx={styles.postsRegion}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
          }}
        >
          {showFeedOnlyContent && !isStaffAccess && (
            <StudentAction theme={theme} />
          )}
        </Box>
        <Box sx={styles.postsList}>
          {showFeedOnlyContent && <ScheduledPostsSummaryContainer />}
          {displayedPosts.map((post) => {
            const isRecentlyPinnedPost =
              !md && isPinnedSelected && recentPinnedPostIds.has(post.objectId);

            return (
              <CommonRoomPostCard
                key={post.objectId}
                post={post}
                readOnly={readOnly}
                isRecentlyPinnedPost={isRecentlyPinnedPost}
                showPinnedPostAlert={
                  showFeedOnlyContent || isRecentlyPinnedPost
                }
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CommonRoomFeedResponsive;
